import type { ReactElement } from 'react'
import { MessageCircle } from 'lucide-react'
import { ContextMenu, ContextMenuContent, ContextMenuLabel, ContextMenuSeparator, ContextMenuTrigger } from '@elmnts/ui/context-menu'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@elmnts/ui/hover-card'
import type { RosterEntry } from '@/hooks/presence/usePresenceRoster'
import type { ChatDoUsuario } from '@/lib/navigation/navegacao'
import { estadoNoCanal } from '@/lib/navigation/presencaNoCanal'
import { UserAvatar } from '@/components/UserAvatar'
import { roleLabel } from '@/lib/format/roleLabel'
import { presenceFullLabel, presenceOf } from '@/lib/presence/presenceTime'
import { cn } from '@/lib/utils'
// Classes `presence-dot--*` da bolinha sobreposta; o componente não é usado aqui.
import '@/components/Presence/PresenceDot/styles.css'
import './styles.css'

/**
 * Puro: o cargo da linha. A 1ª subrole é o cargo, o resto vira `+N`. Sem
 * subrole visível — `null` (leitor sem `users:directory:sub_roles:read`),
 * ausente (back anterior ao campo) ou `[]` — cai no NÍVEL (`role`): é um fato
 * que a tela sabe, e não afirma "sem cargo" sobre quem ela não consegue ver.
 */
export function cargoDaLinha(user: RosterEntry): { cargo: string; extras: string[] } {
  const [primeira, ...resto] = user.sub_roles ?? []
  if (!primeira) return { cargo: roleLabel(user.role), extras: [] }
  return { cargo: primeira.name, extras: resto.map((s) => s.name) }
}

/**
 * Puro: a 2ª linha. Estado + tempo (`presenceFullLabel`) e, quando a pessoa está
 * presente (online/ausente) com canal aberto, ` · Canal X, Canal Y`. Offline ou
 * presença desconhecida (`null`) NUNCA ganha canal.
 */
export function linhaDeEstado(user: RosterEntry, now: number, canais?: readonly string[]): string {
  const base = presenceFullLabel(presenceOf(user), user.last_seen_at, now)
  return presente(user) && canais && canais.length > 0 ? `${base} · ${canais.join(', ')}` : base
}

function presente(user: RosterEntry): boolean {
  return user.presence_status === 'online' || user.presence_status === 'afk'
}

/** Canais abertos mais os canais conhecidos pelos chats, sem duplicar nomes. */
function canaisAbertos(canais: readonly string[] | undefined, chats: readonly ChatDoUsuario[] | undefined): string[] {
  return [...new Set([...(canais ?? []), ...(chats ?? []).flatMap((chat) => (chat.canal ? [chat.canal] : []))])]
    .sort((a, b) => a.localeCompare(b))
}

/**
 * Puro: onde a pessoa está, uma linha por lugar — "No canal X", "Em chat com Y · X",
 * "Em um chat" (sem contato: leitor sem `protocols:read`; sem canal: fora do catálogo).
 * Offline ou desconhecido → `[]` (nunca afirmar onde está quem não está presente).
 */
export function linhasDeOnde(
  user: RosterEntry,
  canais: readonly string[] | undefined,
  chats: readonly ChatDoUsuario[] | undefined,
): string[] {
  if (!presente(user)) return []
  const chatsAbertos = chats ?? []
  const canaisDosChats = new Set(chatsAbertos.flatMap((chat) => (chat.canal ? [chat.canal] : [])))
  const canaisSuprimidos = new Set<string>()
  const noCanal = (canais ?? []).flatMap((canal) => {
    // O canal já aparece junto do contato no item de chat; suprima só essa ocorrência.
    if (canaisDosChats.has(canal) && !canaisSuprimidos.has(canal)) {
      canaisSuprimidos.add(canal)
      return []
    }
    return [`No canal ${canal}`]
  })
  const emChat = chatsAbertos.map((c) => {
    const quem = c.contato ? `Em chat com ${c.contato}` : 'Em um chat'
    return c.canal ? `${quem} · ${c.canal}` : quem
  })
  return [...noCanal, ...emChat]
}

interface PresenceRowProps {
  user: RosterEntry
  now: number
  canais?: readonly string[]
  /** Chats abertos (`useChatsDosUsuarios`): online em chat ganha a bolinha rosa. */
  chats?: readonly ChatDoUsuario[]
}

/** Linha estilo Discord: avatar com status sobreposto, nome + cargo, estado + tempo (+ canal aberto). */
export function PresenceRow({ user, now, canais, chats }: PresenceRowProps) {
  const status = presenceOf(user)
  const { cargo, extras } = cargoDaLinha(user)
  const canaisAtivos = canaisAbertos(canais, chats)
  // Precedência do indicador: ausente (bolinha amarela) vence rosa; só online em chat fica rosa.
  const emChat = estadoNoCanal(user.presence_status, (chats?.length ?? 0) > 0) === 'em_chat'
  return (
    <>
      <span data-estado={emChat ? 'em_chat' : undefined} className="relative shrink-0 rounded-md">
        <UserAvatar size="sm" name={user.name} icon={user.avatar?.icon} color={user.avatar?.color} />
        {/* Indicador decorativo; o estado e o local também aparecem em texto no hover. */}
        <span
          aria-hidden="true"
          className={cn(
            'presence-dot',
            `presence-dot--${status}`,
            'presence-row__status',
            emChat && 'presence-row__status--em-chat',
          )}
        />
      </span>
      <span className={cn('flex min-w-0 flex-1 flex-col', status === 'offline' && 'opacity-60')}>
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium text-foreground">{user.name}</span>
          {/* Cargo encostado à direita: os rótulos alinham em coluna e o nome trunca antes. */}
          <span className="ml-auto flex shrink-0 items-center gap-1">
            <span className="presence-row__cargo">{cargo}</span>
            {extras.length > 0 && (
              <span className="presence-row__cargo" title={extras.join(', ')}>
                {`+${extras.length}`}
              </span>
            )}
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          {presente(user) && canaisAtivos.length > 0 ? (
            <>
              <span className="sr-only">{linhaDeEstado(user, now, canaisAtivos)}</span>
              <span aria-hidden="true" className="flex min-w-0 flex-1 items-center gap-2">
                <span className="min-w-0 truncate">{presenceFullLabel(status, user.last_seen_at, now)}</span>
                <span className="ml-auto max-w-[48%] shrink-0 truncate text-right" title={canaisAtivos.join(', ')}>
                  {canaisAtivos.join(', ')}
                </span>
              </span>
            </>
          ) : (
            <span className="min-w-0 flex-1 truncate">{presenceFullLabel(status, user.last_seen_at, now)}</span>
          )}
        </span>
      </span>
    </>
  )
}

/**
 * Envolve a linha (um elemento DOM: `<button>`, `<div>`, `<li>`) com o hover de onde a
 * pessoa está e o menu do botão direito (user + status + onde está), como a lista de
 * chats faz com o contato. Sem lugar conhecido, o hover não monta; o menu sempre.
 */
export function OndeEstaNaEquipe({
  user,
  now,
  canais,
  chats,
  children,
}: Omit<PresenceRowProps, 'chats'> & { chats?: readonly ChatDoUsuario[]; children: ReactElement }) {
  const linhas = linhasDeOnde(user, canais, chats)
  const canaisAtivos = canaisAbertos(canais, chats)
  const chatsAbertos = presente(user) ? chats ?? [] : []
  const { cargo } = cargoDaLinha(user)
  const gatilho = <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
  return (
    <ContextMenu>
      {linhas.length > 0 ? (
        <HoverCard openDelay={500} closeDelay={150}>
          <HoverCardTrigger asChild>{gatilho}</HoverCardTrigger>
          <HoverCardContent side="left" align="start" className="w-72 p-3">
            <div className="flex items-center gap-2.5">
              <UserAvatar size="sm" name={user.name} icon={user.avatar?.icon} color={user.avatar?.color} />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-semibold text-foreground">{user.name}</span>
                <span className="truncate text-xs text-muted-foreground">@{user.username} · {cargo}</span>
              </div>
            </div>
            <div className="my-2 h-px bg-border" />
            <div className="mt-1 flex min-w-0 items-center justify-between gap-2 text-xs">
              <p className="shrink-0 font-medium text-muted-foreground">
                {presenceFullLabel(presenceOf(user), user.last_seen_at, now)}
              </p>
              {canaisAtivos.length > 0 && (
                <p className="min-w-0 truncate text-right text-muted-foreground" title={canaisAtivos.join(', ')}>
                  {canaisAtivos.join(', ')}
                </p>
              )}
            </div>
            {chatsAbertos.length > 0 && (
              <ul className="mt-1 flex flex-col gap-0.5 text-sm">
                {chatsAbertos.map((chat, i) => {
                  // O canal fica na linha de estado; aqui o ícone substitui “Em chat com”.
                  const rotulo = chat.contato ? `Em chat com ${chat.contato}` : 'Em um chat'
                  const nomeCompleto = chat.canal ? `${rotulo} · ${chat.canal}` : rotulo
                  return (
                    // Dois chats sem contato no mesmo canal dão a mesma linha: índice é a identidade.
                    <li key={i} aria-label={nomeCompleto} className="flex items-center gap-1.5">
                      <MessageCircle size={14} aria-hidden="true" className="shrink-0 text-muted-foreground" />
                      {chat.contato}
                    </li>
                  )
                })}
              </ul>
            )}
          </HoverCardContent>
        </HoverCard>
      ) : (
        gatilho
      )}
      <ContextMenuContent className="min-w-56">
        <ContextMenuLabel className="flex items-center gap-2">
          <UserAvatar size="sm" name={user.name} icon={user.avatar?.icon} color={user.avatar?.color} />
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium">{user.name}</span>
            <span className="truncate text-xs font-normal text-muted-foreground">{cargo}</span>
          </span>
        </ContextMenuLabel>
        <ContextMenuSeparator />
        <p className="px-2 py-1 text-xs text-muted-foreground">{linhaDeEstado(user, now)}</p>
        {linhas.map((l, i) => (
          <p key={i} className="px-2 py-0.5 text-xs">
            {l}
          </p>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  )
}
